import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    App as AntdApp,
    Button,
    Empty,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Typography,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
    FileImageOutlined,
    ImportOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
} from '../../../components/common/PaginatedListTable'
import type {
    ValidationDataset,
    ValidationDatasetSample,
    ValidationDatasetVersion,
} from '../../../types/modelValidation'

interface ValidationDatasetDetailModalProps {
    canEvaluate: boolean
    dataset: ValidationDataset | null
    onClose: () => void
    onOpenDatasetImport: () => void
    onOpenHistory: () => void
    onPublish: () => Promise<void>
    sampleColumns: TableColumnsType<ValidationDatasetSample>
    saving: boolean
    version: ValidationDatasetVersion | null
}

const ValidationDatasetDetailModal: React.FC<
    ValidationDatasetDetailModalProps
> = ({
    canEvaluate,
    dataset,
    onClose,
    onOpenDatasetImport,
    onOpenHistory,
    onPublish,
    sampleColumns,
    saving,
    version,
}) => {
    const { modal } = AntdApp.useApp()
    const { t } = useTranslation(['admin', 'common'])
    const [draftImageKeyword, setDraftImageKeyword] = useState('')
    const [draftReviewFilter, setDraftReviewFilter] = useState<boolean>()
    const [imageKeyword, setImageKeyword] = useState('')
    const [reviewFilter, setReviewFilter] = useState<boolean>()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)
    const reviewedSampleCount =
        version?.samples.filter((sample) => sample.annotation_reviewed)
            .length || 0
    const pendingReviewCount = Math.max(
        (version?.sample_count || 0) - reviewedSampleCount,
        0,
    )
    const editable = canEvaluate && version?.status === 'draft'
    const publishBlockedMessage = !editable
        ? null
        : version.sample_count === 0
          ? t('admin:modelValidation.datasets.publishBlockedNoImages')
          : pendingReviewCount > 0
            ? t('admin:modelValidation.datasets.publishBlockedPendingReview', {
                  count: pendingReviewCount,
              })
            : null
    const filteredSamples = useMemo(() => {
        const normalizedKeyword = imageKeyword.trim().toLowerCase()
        return (version?.samples || []).filter(
            (sample) =>
                (!normalizedKeyword ||
                    sample.filename
                        .toLowerCase()
                        .includes(normalizedKeyword)) &&
                (reviewFilter === undefined ||
                    sample.annotation_reviewed === reviewFilter),
        )
    }, [imageKeyword, reviewFilter, version?.samples])

    useEffect(() => {
        setDraftImageKeyword('')
        setDraftReviewFilter(undefined)
        setImageKeyword('')
        setReviewFilter(undefined)
        setPage(1)
        setPageSize(20)
    }, [dataset?.stable_id, version?.version_no])

    const applyFilters = () => {
        setImageKeyword(draftImageKeyword.trim())
        setReviewFilter(draftReviewFilter)
        setPage(1)
    }

    const resetFilters = () => {
        setDraftImageKeyword('')
        setDraftReviewFilter(undefined)
        setImageKeyword('')
        setReviewFilter(undefined)
        setPage(1)
    }

    return (
        <Modal
            centered
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>
                        {t('admin:modelValidation.actions.close')}
                    </Button>
                    {editable ? (
                        <Button
                            disabled={Boolean(publishBlockedMessage)}
                            loading={saving}
                            onClick={() =>
                                modal.confirm({
                                    cancelText: t(
                                        'admin:modelValidation.actions.cancel',
                                    ),
                                    okText: t(
                                        'admin:modelValidation.actions.completeAndEnable',
                                    ),
                                    title: t(
                                        'admin:modelValidation.datasets.publishConfirm',
                                    ),
                                    maskClosable: false,
                                    onOk: onPublish,
                                })
                            }
                            type="primary"
                        >
                            {t(
                                'admin:modelValidation.actions.completeAndEnable',
                            )}
                        </Button>
                    ) : null}
                </Space>
            }
            onCancel={onClose}
            maskClosable={false}
            open={Boolean(dataset)}
            styles={{
                body: {
                    maxHeight: 'calc(100dvh - 160px)',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                },
            }}
            style={{ maxWidth: 'calc(100vw - 24px)' }}
            title={t(
                editable
                    ? 'admin:modelValidation.datasets.imagesTitle'
                    : 'admin:modelValidation.actions.viewImages',
            )}
            width={1440}
        >
            {dataset ? (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%' }}
                >
                    {version ? (
                        <>
                            <div>
                                <Typography.Title
                                    level={5}
                                    style={{ margin: 0 }}
                                >
                                    {t('admin:modelValidation.datasets.images')}
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    {t(
                                        'admin:modelValidation.datasets.versionSummary',
                                        {
                                            samples: version.sample_count,
                                            reviewed: reviewedSampleCount,
                                        },
                                    )}
                                </Typography.Text>
                            </div>

                            {publishBlockedMessage ? (
                                <Alert
                                    message={publishBlockedMessage}
                                    showIcon
                                    type="warning"
                                />
                            ) : null}

                            <FilterBar>
                                <FilterField
                                    label={t(
                                        'admin:modelValidation.filters.imageName',
                                    )}
                                >
                                    <Input
                                        allowClear
                                        onChange={(event) =>
                                            setDraftImageKeyword(
                                                event.target.value,
                                            )
                                        }
                                        onPressEnter={applyFilters}
                                        placeholder={t(
                                            'admin:modelValidation.datasets.imageNamePlaceholder',
                                        )}
                                        style={{ width: 220 }}
                                        value={draftImageKeyword}
                                    />
                                </FilterField>
                                <FilterField
                                    label={t(
                                        'admin:modelValidation.filters.reviewed',
                                    )}
                                >
                                    <Select
                                        allowClear
                                        onChange={setDraftReviewFilter}
                                        options={[
                                            {
                                                label: t(
                                                    'admin:modelValidation.datasets.reviewed',
                                                ),
                                                value: true,
                                            },
                                            {
                                                label: t(
                                                    'admin:modelValidation.datasets.pendingReview',
                                                ),
                                                value: false,
                                            },
                                        ]}
                                        placeholder={t(
                                            'admin:modelValidation.filters.reviewed',
                                        )}
                                        style={{ width: 150 }}
                                        value={draftReviewFilter}
                                    />
                                </FilterField>
                                <FilterActions>
                                    <Button
                                        aria-label={t('common:buttons.search')}
                                        icon={<SearchOutlined />}
                                        onClick={applyFilters}
                                        type="primary"
                                    >
                                        {t('common:buttons.search')}
                                    </Button>
                                    <Button
                                        aria-label={t('common:buttons.reset')}
                                        onClick={resetFilters}
                                    >
                                        {t('common:buttons.reset')}
                                    </Button>
                                </FilterActions>
                            </FilterBar>

                            <PaginatedListContainer>
                                {editable ? (
                                    <PaginatedListToolbar align="left">
                                        <Button
                                            aria-label={t(
                                                'admin:modelValidation.actions.selectImages',
                                            )}
                                            icon={<FileImageOutlined />}
                                            onClick={onOpenHistory}
                                        >
                                            {t(
                                                'admin:modelValidation.actions.selectImages',
                                            )}
                                        </Button>
                                        <Button
                                            aria-label={t(
                                                'admin:modelValidation.actions.importDatasetImages',
                                            )}
                                            icon={<ImportOutlined />}
                                            onClick={onOpenDatasetImport}
                                        >
                                            {t(
                                                'admin:modelValidation.actions.importDatasetImages',
                                            )}
                                        </Button>
                                    </PaginatedListToolbar>
                                ) : null}

                                <PaginatedListCard>
                                    <Table
                                        columns={sampleColumns}
                                        dataSource={filteredSamples}
                                        pagination={buildPaginatedListPagination({
                                            current: page,
                                            pageSize,
                                            showTotal: (total) =>
                                                t(
                                                    'admin:modelValidation.pagination.totalImages',
                                                    { total },
                                                ),
                                            total: filteredSamples.length,
                                            onChange: (
                                                nextPage,
                                                nextPageSize,
                                            ) => {
                                                setPage(
                                                    nextPageSize !== pageSize
                                                        ? 1
                                                        : nextPage,
                                                )
                                                setPageSize(nextPageSize)
                                            },
                                        })}
                                        rowKey="stable_id"
                                        scroll={{
                                            x: 860,
                                            y: 'max(240px, calc(100dvh - 500px))',
                                        }}
                                        size={PAGINATED_LIST_TABLE_SIZE}
                                        sticky
                                    />
                                </PaginatedListCard>
                            </PaginatedListContainer>
                        </>
                    ) : (
                        <Empty />
                    )}
                </Space>
            ) : null}
        </Modal>
    )
}

export default ValidationDatasetDetailModal
